import { yup } from '@framework/validation';
import { evaluateExpression } from '../schema/expressions/evaluate/evaluateExpression';
import { mapReferenceToPath } from '../schema/expressions/evaluate/mapReferenceToPath';
import { createEvaluationVariables } from '../schema/expressions/evaluate/variables/IEvaluationVariables';
import { collectExpressionReferences } from '../schema/expressions/references/collectExpressionReferences';
import { ICodeWidget } from '../schema/widgets/ICodeWidget';
import { IParentWidgetScope, IWidgetType } from '../schema/widgets/IWidgetType';
import { IWidgetOptionValue, Widget } from '../schema/widgets/Widget';
import { multiSelectOptionsObjectName } from '../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName';
import { WidgetConditionManager } from './WidgetConditionManager';
import { remapBooleanConditionReferencesUpwardInScope } from './utils/remapReferencesUpwardInScope';

/**
 * Applica la condizione di visibilità ad uno schema yup
 */
export function setVisibleCondition<W extends Widget, S extends yup.AnySchema>(
  schema: S,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope = [],
) {
  const conditionData = WidgetConditionManager.mergeWithScope(
    widget,
    'visible',
    parentScope,
  );

  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema
      : schema.when([], (_, schema) => schema.notRequired().strip());
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);

    const isVisible = evaluateExpression(condition.expression, { variables });

    return isVisible ? schema : schema.notRequired().strip();
  });
}

/**
 * Funzione che applica la condizione di visualizzazione allo schema
 * di una singola opzione di un widget.
 */
export function setOptionVisibleCondition<
  W extends ICodeWidget,
  S extends yup.AnySchema,
>(schema: S, widget: W, option: IWidgetOptionValue) {
  const condition = remapBooleanConditionReferencesUpwardInScope(
    { code: multiSelectOptionsObjectName(widget.code) },
    option.visible,
  );

  if (!condition) return schema;
  if (condition.type === 'static') {
    return condition.value ? schema : schema.notRequired().strip();
  }

  const references = collectExpressionReferences(condition.expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);
    const isVisible = evaluateExpression(condition.expression, { variables });

    return isVisible ? schema : schema.notRequired().strip();
  });
}
